import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowLeft, Trash2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import OrderSuccessModal from '@/components/OrderSuccessModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = ({ user, onLogout, cart: cartProp }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [observations, setObservations] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [successOrder, setSuccessOrder] = useState(null);
  const [successSavings, setSuccessSavings] = useState(0);
  const [successListTotal, setSuccessListTotal] = useState(0);

  const isStaff = user && (user.role === 'admin' || user.role === 'manager');

  // Load clients so admin/manager can place orders on behalf of a client
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'manager')) {
      const token = localStorage.getItem('token');
      axios
        .get(`${API}/admin/clients-for-order`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setClients(res.data || []))
        .catch(() => setClients([]));
    }
  }, [user]);

  // Update local cart state when prop changes
  useEffect(() => {
    if (cartProp && cartProp.length > 0) {
      setCart(cartProp);
    } else {
      loadCart();
    }
  }, [cartProp]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const updateQuantity = (variantId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cart.map(item => 
      item.variant_id === variantId ? { ...item, quantity: newQuantity } : item
    );
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const updateItemDeliveryDate = (variantId, date) => {
    const updatedCart = cart.map(item =>
      item.variant_id === variantId ? { ...item, delivery_date: date } : item
    );
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
  };

  const applyDeliveryDateToAll = (date) => {
    const updatedCart = cart.map(item => (item.is_shipping ? item : { ...item, delivery_date: date }));
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Fecha aplicada a todas las líneas');
  };

  const removeItem = (variantId) => {
    const updatedCart = cart.filter(item => item.variant_id !== variantId);
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Producto eliminado del carrito');
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getListTotal = () => {
    // Sum of PVP tarifa (Q) — used to display total savings
    return cart.reduce((sum, item) => {
      if (item.is_shipping) return sum + item.price * item.quantity;
      const list = typeof item.list_price === 'number' && item.list_price > 0 ? item.list_price : item.price;
      return sum + list * item.quantity;
    }, 0);
  };

  const getSavings = () => Math.max(0, getListTotal() - getTotal());

  const IVA_RATE = 0.21;
  const getTotalWithIva = () => getTotal() * (1 + IVA_RATE);

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const needsShipping = () => {
    // Sudeporte/MACRON — no aplica la lógica de portes mínimos de HI-TEC.
    // Un solo cliente autorizado (Deportes Tomás) con condiciones acordadas.
    return false;
  };

  const addShippingToCart = () => {
    const shippingItem = {
      variant_id: 'SHIPPING',
      reference: 'SHIPPING',
      ean: 'SHIPPING',
      model_name: 'Portes',
      color: '',
      size: '',
      quantity: 1,
      price: 10,
      image: '',
      is_shipping: true
    };
    
    const updatedCart = [...cart, shippingItem];
    setCart(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    toast.success('Portes añadidos al carrito (10€)');
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Check if shipping is needed
    if (needsShipping()) {
      toast.error('El pedido mínimo es de 150€ para portes pagados. Por favor añade más productos o acepta los portes de 10€.');
      return;
    }

    setLoading(true);

    try {
      // Ensure all cart items have required fields, including reference
      const validatedItems = cart.map(item => ({
        variant_id: item.variant_id,
        reference: item.reference || item.ean || 'N/A', // Fallback to EAN or N/A if reference missing
        ean: item.ean,
        model_name: item.model_name,
        color: item.color,
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        list_price: typeof item.list_price === 'number' ? item.list_price : null,
        delivery_date: item.delivery_date || deliveryDate || null,
      }));

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/orders`,
        { 
          items: validatedItems,
          delivery_date: deliveryDate || null,
          observations: observations.trim() || null,
          target_user_id: selectedClientId || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assignedClient = clients.find((c) => c.id === selectedClientId);
      const createdOrder = response.data;
      // Snapshot savings BEFORE we clear the cart
      const savings = getSavings();
      const listTotal = getListTotal();

      toast.success(
        assignedClient
          ? `¡Pedido realizado a nombre de ${assignedClient.business_name}!`
          : '¡Pedido realizado exitosamente!'
      );
      localStorage.removeItem('cart');
      setCart([]);
      setObservations('');
      setDeliveryDate('');
      setSelectedClientId('');
      window.dispatchEvent(new Event('cartUpdate'));
      setSuccessOrder({ ...createdOrder, delivery_date_global: deliveryDate || null });
      setSuccessSavings(savings);
      setSuccessListTotal(listTotal);
    } catch (error) {
      console.error('Error placing order:', error);
      
      // Handle validation errors properly - convert everything to string
      let errorMessage = 'Error al realizar el pedido. Por favor, inténtalo de nuevo.';
      
      try {
        if (error.response && error.response.data && error.response.data.detail) {
          const detail = error.response.data.detail;
          
          if (Array.isArray(detail)) {
            // Validation errors array
            const errors = detail.map(err => {
              const field = Array.isArray(err.loc) ? err.loc.join('.') : 'campo';
              const msg = err.msg || 'error';
              return field + ': ' + msg;
            });
            errorMessage = 'Error de validación: ' + errors.join(', ');
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          } else {
            errorMessage = 'Error al procesar el pedido';
          }
        }
      } catch (parseError) {
        console.error('Error parsing error:', parseError);
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/catalog" className="flex items-center space-x-2 text-white hover:text-neutral-300 transition">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver al catálogo</span>
            </Link>
            <Link to="/" className="flex items-center space-x-3">
              <img src="/sudeporte-logo.png" alt="Sudeporte" className="h-9" />
              <span className="hidden sm:block h-8 w-px bg-neutral-700"></span><img src="/macron-msh-neg-h44.png" alt="Macron Sports Hub" className="hidden sm:block h-7" />
            </Link>
          </div>
        </div>
      </header>

      {/* Cart Content */}
      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-black mb-8">Carrito de Compras</h1>

        {cart.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-black mb-2">Tu carrito está vacío</h2>
            <p className="text-gray-600 mb-6">Agrega productos desde el catálogo</p>
            <Link to="/catalog">
              <Button data-testid="go-to-catalog-btn" className="bg-black hover:bg-neutral-800 text-white">
                Ir al Catálogo
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <div 
                  key={item.variant_id} 
                  data-testid={`cart-item-${item.variant_id}`}
                  className="bg-white rounded-xl shadow-md border border-gray-200 p-6"
                >
                  <div className="flex items-center space-x-4">
                    {/* Product Image */}
                    <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.model_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-black">{item.model_name}</h3>
                      <p className="text-xs text-neutral-500 font-mono mb-1">
                        REF: {item.reference}
                      </p>
                      <p className="text-gray-600 text-sm">
                        Color: {item.color} | Talla: {item.size}
                      </p>
                      <p className="text-gray-500 text-xs">EAN: {item.ean}</p>

                      {/* Per-line delivery date */}
                      {!item.is_shipping && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <label className="text-xs uppercase tracking-widest text-neutral-500">
                            📅 Entrega
                          </label>
                          <input
                            type="date"
                            data-testid={`delivery-date-${item.variant_id}`}
                            value={item.delivery_date || ''}
                            min={new Date().toISOString().split('T')[0]}
                            onChange={(e) => updateItemDeliveryDate(item.variant_id, e.target.value)}
                            className="text-sm px-2 py-1 border border-neutral-300 rounded-md focus:border-black focus:outline-none"
                          />
                          {item.delivery_date && (
                            <button
                              type="button"
                              onClick={() => updateItemDeliveryDate(item.variant_id, '')}
                              className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-black"
                            >
                              Quitar fecha
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-3">
                      <Button
                        data-testid={`decrease-qty-${item.variant_id}`}
                        onClick={() => updateQuantity(item.variant_id, item.quantity - 1)}
                        variant="outline"
                        size="icon"
                      >
                        -
                      </Button>
                      <span className="text-lg font-semibold w-8 text-center">{item.quantity}</span>
                      <Button
                        data-testid={`increase-qty-${item.variant_id}`}
                        onClick={() => updateQuantity(item.variant_id, item.quantity + 1)}
                        variant="outline"
                        size="icon"
                      >
                        +
                      </Button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <p className="text-xl font-black text-black">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500">
                        €{item.price.toFixed(2)} c/u
                      </p>
                    </div>

                    {/* Remove Button */}
                    <Button
                      data-testid={`remove-item-${item.variant_id}`}
                      onClick={() => removeItem(item.variant_id)}
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-black mb-6">Resumen del Pedido</h2>

                {/* Client selector - admin/manager can place orders on behalf of a client */}
                {isStaff && (
                  <div className="mb-6 bg-neutral-50 border-2 border-neutral-300 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-neutral-900 mb-2">
                      👤 Asignar pedido a un cliente
                    </label>
                    <select
                      data-testid="client-select"
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg bg-white focus:border-black focus:outline-none"
                    >
                      <option value="">— Sin cliente (pedido propio) —</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.business_name}{c.customer_code ? ` (${c.customer_code})` : ''} · {c.order_count} pedidos
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-neutral-700 mt-2">
                      {selectedClientId
                        ? 'El pedido se registrará a nombre del cliente seleccionado y recibirá su confirmación.'
                        : `Selecciona un cliente para grabar el pedido en su nombre (${clients.length} clientes).`}
                    </p>
                  </div>
                )}
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-neutral-500 text-sm line-through">
                    <span>PVP Tarifa</span>
                    <span>€{getListTotal().toFixed(2)}</span>
                  </div>
                  {getSavings() > 0 && (
                    <div className="flex justify-between text-emerald-700 font-bold text-sm bg-emerald-50 -mx-2 px-2 py-1 rounded" data-testid="cart-savings">
                      <span>Ahorro por descuento 24%</span>
                      <span>− €{getSavings().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-neutral-700">
                    <span>Subtotal (base)</span>
                    <span data-testid="cart-subtotal">€{getTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-500 text-sm">
                    <span>IVA 21%</span>
                    <span>€{(getTotal() * IVA_RATE).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-500 text-sm">
                    <span>Artículos</span>
                    <span>{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>
                  </div>
                  <div className="border-t border-neutral-200 pt-3 flex justify-between text-2xl font-black text-black">
                    <span>Total IVA incl.</span>
                    <span data-testid="cart-total">€{getTotalWithIva().toFixed(2)}</span>
                  </div>
                </div>

                {/* Global delivery date — applied to all lines */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    📅 Fecha de entrega global (opcional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="flex-1 px-3 py-3 border-2 border-neutral-300 rounded-lg focus:border-black focus:outline-none"
                    />
                    <button
                      type="button"
                      data-testid="apply-global-date"
                      disabled={!deliveryDate}
                      onClick={() => applyDeliveryDateToAll(deliveryDate)}
                      className="px-3 py-2 text-xs uppercase tracking-widest border border-black text-black rounded-lg hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Aplicar a todo
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    Cada línea puede tener su propia fecha; usa &quot;Aplicar a todo&quot; para copiar ésta a todas.
                  </p>
                </div>

                {/* Observations Field */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Observaciones (Opcional)
                  </label>
                  <textarea
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    placeholder="Añade cualquier comentario o instrucción especial para tu pedido..."
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black focus:outline-none resize-none"
                    rows="3"
                    maxLength="500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {observations.length}/500 caracteres
                  </p>
                </div>

                {/* Shipping Notice */}
                {needsShipping() && (
                  <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-4 mb-4">
                    <p className="text-orange-900 font-semibold mb-2">📦 Portes Necesarios</p>
                    <p className="text-sm text-orange-800 mb-3">
                      El pedido mínimo para portes pagados es de 150€. 
                      Tu pedido actual: <strong>€{calculateTotal().toFixed(2)}</strong>
                    </p>
                    <Button
                      onClick={addShippingToCart}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="sm"
                    >
                      Añadir Portes (10€)
                    </Button>
                  </div>
                )}

                {user && !isStaff && !user.approved ? (
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                    <p className="text-yellow-900 font-semibold mb-2">⏳ Cuenta Pendiente de Aprobación</p>
                    <p className="text-sm text-yellow-800">
                      No puedes realizar pedidos hasta que tu cuenta sea aprobada por un administrador.
                    </p>
                  </div>
                ) : (
                  <>
                    <Button
                      data-testid="checkout-btn"
                      onClick={handleCheckout}
                      disabled={loading || needsShipping()}
                      className="w-full bg-black hover:bg-neutral-800 py-6 text-lg text-white font-bold disabled:bg-neutral-400"
                    >
                      {loading ? 'Procesando...' : needsShipping() ? 'Añade portes o más productos' : 'Realizar Pedido'}
                    </Button>

                    {!needsShipping() && calculateTotal() >= 150 && (
                      <p className="text-xs text-green-600 text-center mt-2 font-semibold">
                        ✓ Portes pagados (pedido ≥ 150€)
                      </p>
                    )}

                    <p className="text-xs text-gray-500 text-center mt-4">
                      Recibirás una confirmación por email
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />

      {successOrder && (
        <OrderSuccessModal
          order={successOrder}
          savings={successSavings}
          listTotal={successListTotal}
          deliveryDate={successOrder.delivery_date_global}
          onContinueShopping={() => {
            setSuccessOrder(null);
            navigate('/catalog');
          }}
        />
      )}
    </div>
  );
};

export default Cart;
